import { useState, useEffect } from 'react';
import { subscriptionApi } from '@/services/api';
import type { Subscription, Plan } from '@/types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ProductHuntMark, TrialTimeline } from '@/components/marketing/OfferParts';
import UpgradeModal from '@/components/subscription/UpgradeModal';
import { offerPeriodKey, useProductHuntOffer } from '@/hooks/useProductHuntOffer';

import { useTranslation } from 'react-i18next';
function getActiveTrialDaysLeft(subscription: Subscription): number | null {
  if (subscription.status !== 'active' || !subscription.trial_ends_at) {
    return null;
  }

  const endDate = new Date(subscription.trial_ends_at);
  const now = new Date();
  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays >= 0 ? diffDays : null;
}

export function FreeTrialBanner() {
    const { t } = useTranslation();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentBoardCount, setCurrentBoardCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [scheduledUsage, setScheduledUsage] = useState<{ current: number; max: number } | null>(null);
  const offer = useProductHuntOffer();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subRes, plansRes] = await Promise.all([
          subscriptionApi.getCurrent(),
          subscriptionApi.getPlans()
        ]);

        if (plansRes.data) {
          setPlans(plansRes.data);
        }

        if (subRes.data?.subscription) {
          const sub = subRes.data.subscription;
          setSubscription(sub);
          setCurrentBoardCount(subRes.data.usage?.boards.current ?? 0);
          setDaysLeft(getActiveTrialDaysLeft(sub));

          // Only relevant while on an active trial — fetched after we know
          // that, so a paid subscriber's dashboard never makes this call.
          if (getActiveTrialDaysLeft(sub) !== null) {
            try {
              const limitRes = await subscriptionApi.checkLimit('schedule_content');
              const data = limitRes.data;
              if (data?.is_trial && typeof data.current === 'number' && typeof data.max === 'number') {
                setScheduledUsage({ current: data.current, max: data.max });
              }
            } catch (limitError) {
              console.error('Failed to fetch scheduled content usage for banner:', limitError);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch data for banner:', error);
      }
    };

    fetchData();
  }, []);

  // Only show the banner if there's a trial, there are days left, and the user hasn't dismissed it
  if (!isVisible || daysLeft === null || !subscription) {
    return null;
  }

  // An offer this account can still use replaces the plain trial reminder: it is
  // the more useful thing to know, and paying early costs no trial days.
  const day = 1000 * 60 * 60 * 24;
  const offerDaysLeft = offer?.deadline
    ? Math.max(1, Math.ceil((new Date(offer.deadline).getTime() - Date.now()) / day))
    : null;
  const trialLength = subscription.trial_ends_at
    ? Math.max(daysLeft, Math.round((new Date(subscription.trial_ends_at).getTime() - new Date(subscription.created_at).getTime()) / day)) || 14
    : 14;
  const showOffer = Boolean(offer && offerDaysLeft !== null);

  return (
    <div data-promo-hide className="relative z-50 border-b border-white/10 bg-primary-950 text-white">
      <div className="mx-auto flex min-h-11 items-center gap-3 py-2 pl-4 pr-12 text-sm sm:pl-6 lg:pl-8">
        {showOffer && <ProductHuntMark className="h-2.5 w-2.5 shrink-0" />}
        <TrialTimeline
          total={trialLength}
          left={Math.min(trialLength, showOffer ? offerDaysLeft! : daysLeft)}
          paid={showOffer}
          tone="onDark"
          className="hidden h-3 w-28 shrink-0 md:flex"
        />
        <p className="min-w-0 flex-1 truncate leading-5 text-white/75 sm:whitespace-normal">
          {offer && showOffer ? (
            <>
              {/* Phones get one short line so the strip stays a single row. */}
              <strong className="font-semibold text-white sm:hidden">{t('trial.offerShort', { percentage: offer.percentage, count: offerDaysLeft! })}</strong>
              <span className="hidden sm:inline">
                <strong className="font-semibold text-white">{t('trial.offerTitle', { percentage: offer.percentage, period: t(offerPeriodKey(offer.intervals)) })}</strong>{' '}
                {t('trial.offerEnds', { count: offerDaysLeft! })}
                <span className="hidden lg:inline"> {t('trial.offerCarryOver')}</span>
              </span>
            </>
          ) : (
            <>
              <strong className="font-semibold text-white">{t('trial.daysLeft', { count: daysLeft })}</strong>
              {scheduledUsage && (
                <span className="ml-2 hidden tabular-nums sm:inline">
                  {t('trial.scheduledContentUsage', { current: scheduledUsage.current, max: scheduledUsage.max })}
                </span>
              )}
            </>
          )}
        </p>
        <button
          onClick={() => setShowUpgradeModal(true)}
          className="flex-none rounded-md bg-white px-3 py-1 text-[13px] font-semibold text-primary-950 transition-colors hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-950"
        >
          {offer ? (
            <>
              <span className="sm:hidden">{t('trial.useOfferShort')}</span>
              <span className="hidden sm:inline">{t('trial.useOffer', { percentage: offer.percentage })}</span>
            </>
          ) : t('trial.upgradeNow')}
        </button>
      </div>
      <button
        type="button"
        className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        onClick={() => setIsVisible(false)}
      >
        <span className="sr-only">{t('common.dismiss')}</span>
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>

      {showUpgradeModal && subscription && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={subscription.plan?.tier || 'free'}
          plans={plans}
          isTrial={true}
          trialDaysLeft={daysLeft}
          currentBoardCount={currentBoardCount}
        />
      )}
    </div>
  );
}
